import type { TranslationKeys } from "./en"

export const de: TranslationKeys = {
  // App
  appName: "DevPulse",

  // Navigation
  navDashboard: "Dashboard",
  navContributors: "Mitwirkende",
  navTeams: "Teams",
  navRepositories: "Repositories",

  // Views
  viewOrganization: "Organisation",
  viewGroup: "Gruppe",
  viewIndividual: "Individuell",
  viewSelectPlaceholder: "Ansicht wählen",

  // Common
  loading: "Daten werden geladen...",
  authenticating: "Authentifizierung läuft...",
  loadingError: "Fehler beim Laden der Daten",
  authError: "Authentifizierungsfehler",
  retry: "Erneut versuchen",
  tryAgain: "Nochmal versuchen",
  profile: "Profil",
  settings: "Einstellungen",
  logout: "Abmelden",
  selectTimeRange: "Zeitraum wählen",
  selectTeam: "Team auswählen...",
  selectDeveloper: "Entwickler auswählen...",

  // Time ranges
  timeLast7Days: "Letzte 7 Tage",
  timeLast30Days: "Letzte 30 Tage",
  timeAll: "Alle",

  // Dashboard titles
  dashboardOrganizationTitle: "Organisations-Dashboard",
  dashboardOrganizationSubtitle:
    "Übersicht der Entwicklungsmetriken über alle Repositories und Teammitglieder hinweg",
  dashboardTeamTitle: "Team-Dashboard",
  dashboardTeamSubtitle: "Entwicklungsmetriken für {team} Teammitglieder",
  dashboardTeamNoData:
    "Keine Teams konfiguriert. Bitte fügen Sie Teamdaten hinzu, um teamspezifische Metriken anzuzeigen.",
  dashboardIndividualTitle: "Individuelles Dashboard",
  dashboardIndividualSubtitle: "Entwicklungsmetriken für {author}",
  dashboardIndividualNoData:
    "Keine Autoren gefunden. Bitte laden Sie Daten, um individuelle Metriken anzuzeigen.",

  // Contributors page
  contributorsTitle: "Mitwirkende",
  contributorsSubtitle: "Leistung und Aktivität der Mitwirkenden analysieren",
  contributorsNoData: "Keine Mitwirkendendaten für den ausgewählten Zeitraum verfügbar.",

  // Teams page
  teamsTitle: "Teams",
  teamsSubtitle: "Teamleistung und -aktivität analysieren",
  teamsNoData: "Keine Teamdaten für den ausgewählten Zeitraum verfügbar.",

  // Repositories page
  repositoriesTitle: "Repositories",
  repositoriesSubtitle: "Repository-Leistung und -Aktivität analysieren",
  repositoriesNoData: "Keine Repository-Daten für den ausgewählten Zeitraum verfügbar.",
  repositoriesScoreTitle: "Repository-Commit-Komplexitätsscore",
  repositoriesScoreDescription: "Gesamtkomplexitätsscore aller Commits pro Repository.",
  repositoriesPRsTitle: "Repository Pull Requests",
  repositoriesPRsDescription: "Anzahl der zusammengeführten Pull Requests pro Repository.",

  // DORA Metrics page
  doraMetricsTitle: "DORA-Metriken",
  doraMetricsSubtitle:
    "DevOps Research and Assessment (DORA) Metriken zur Messung der Software-Delivery-Performance",
  doraNoDataTitle: "Keine DORA-Metrikdaten verfügbar",
  doraNoDataDescription:
    "DORA-Metriken erfordern Releases, Pull Requests und Commit-Daten. Bitte stellen Sie sicher, dass Daten gesammelt und exportiert werden.",
  doraMissingDataTitle: "Fehlende Daten für vollständige DORA-Metriken",
  doraMissingDataDescription:
    "Die folgenden Datenpunkte fehlen oder sind unvollständig für die vollständige DORA-Metrikberechnung:",
  doraDataLimitationsTitle: "Datenbeschränkungen",
  doraDataLimitationsDescription:
    "Die angezeigten DORA-Metriken werden aus verfügbaren Daten berechnet, die einige Einschränkungen haben:",
  doraOverallPerformance: "Gesamtes DORA-Leistungsniveau",
  doraOverallDescription:
    "Basierend auf den vier Schlüsselmetriken wird das Gesamtleistungsniveau Ihres Teams gemäß DORA-Benchmarks berechnet.",
  doraDeploymentFrequency: "Deployment-Frequenz",
  doraLeadTime: "Durchlaufzeit für Änderungen",
  doraChangeFailureRate: "Änderungs-Fehlerrate",
  doraTimeToRestore: "Zeit zur Wiederherstellung des Dienstes",
  doraPerMonth: "pro Monat",
  doraDays: "Tage",
  doraHours: "Stunden",
  doraDeploymentsByMonth: "Deployments pro Monat",
  doraLeadTimeTrend: "Durchlaufzeit-Trend",
  doraFailureRateTrend: "Fehlerrate-Trend",
  doraRestoreTimeTrend: "Wiederherstellungszeit-Trend",
  doraDeploymentFrequencyDetail: "Deployment-Frequenz über Zeit",
  doraDeploymentFrequencyDetailDescription: "Anzahl der Deployments pro Monat",
  doraLeadTimeDetail: "Durchlaufzeit für Änderungen über Zeit",
  doraLeadTimeDetailDescription:
    "Durchschnittliche Zeit von PR-Erstellung bis Merge (in Stunden) pro Monat",
  doraMethodologyTitle: "Methodik & Datenquellen",
  doraDeploymentFrequencyMethodology:
    "Berechnet aus veröffentlichten Releases (ohne Entwürfe und Pre-Releases) in der Releases-Datenbanktabelle.",
  doraLeadTimeMethodology:
    "Berechnet als Zeit zwischen Pull-Request-Erstellung und Merge, gemessen aus der pull_requests-Tabelle.",
  doraChangeFailureRateMethodology:
    "Geschätzt durch Erkennung von Bugfix- oder Revert-Commits innerhalb von 7 Tagen nach einem Release. Dies ist eine Annäherung, da wir keine explizite Deployment-Fehler-Verfolgung haben.",
  doraTimeToRestoreMethodology:
    "Geschätzt als Zeit zwischen einem Release und dem ersten Bugfix/Revert-Commit. Dies ist eine Annäherung, da wir keine Incident-Management-Daten haben.",

  // DORA Performance Levels
  doraLevelElite: "ELITE",
  doraLevelHigh: "HOCH",
  doraLevelMedium: "MITTEL",
  doraLevelLow: "NIEDRIG",

  // DORA Missing Data Points
  doraMissingReleases: "Release-Daten für Deployment-Häufigkeit",
  doraMissingPullRequests: "Pull-Request-Daten für Lead-Time-Berechnung",
  doraMissingCommits: "Commit-Daten für Fehleranalyse",
  doraMissingDeployments: "Deployment-Daten (Phase 2) - verwenden Releases als Proxy",
  doraMissingWorkflowRuns: "CI/CD-Workflow-Daten (Phase 2) - eingeschränkte Pipeline-Sichtbarkeit",
  doraMissingIncidents: "Incident-Tracking-Daten (Phase 3) - verwenden Bugfix-Commits als Proxy",

  error: "Fehler",

  // Tabs
  tabPerformance: "Leistung",
  tabScore: "Bewertung",
  tabPRs: "PRs",
  tabIssues: "Issues",
  tabActivity: "Aktivität",
  tabReleases: "Releases",

  // Activity
  recentActivity: "Letzte Aktivität",

  // Releases tab
  releasesTitle: "Releases (Letzte 12 Wochen)",
  releasesDescription: "Gesamtzahl der veröffentlichten Releases in allen Repositories",
  releasesChartTitle: "Releases nach Repository (Letzte 6 Wochen)",
  releasesChartDescription: "Wöchentliche Release-Aktivität pro Repository",

  // Performance tab
  performanceScoreTitle: "Gesamt-Leistungsbewertung (0-100)",
  performanceScoreDescription:
    "Eine umfassende Metrik, die Codequalität (30%), Liefergeschwindigkeit (25%), Code-Impact (20%), Problemlösung (15%) und Arbeitsvielfalt (10%) kombiniert.",
  performanceScoreAverage: "Durchschnitt: {value}",
  performanceScoreComparison:
    " — Der Prozentsatz zeigt, wie jeder Mitwirkende im Vergleich zu diesem Durchschnitt abschneidet.",

  performanceScoreTeamTitle: "Gesamt-Team-Leistungsbewertung (0-100)",
  performanceScoreTeamDescription:
    "Aggregierte Leistungsbewertung, die alle Beiträge der Teammitglieder über Codequalität, Liefergeschwindigkeit, Code-Impact, Problemlösung und Arbeitsvielfalt hinweg kombiniert.",

  // Score tab
  scoreTitle: "Commit-Komplexitätsbewertung",
  scoreDescription:
    "Misst die Code-Komplexität basierend auf geänderten Dateien, modifizierten Zeilen, Funktionsänderungen und zyklomatischer Komplexität. Höhere Bewertungen weisen auf anspruchsvollere und wirkungsvollere Beiträge hin.",
  scoreAverage: "Durchschnitt: {value} Punkte",
  scoreComparison:
    " — Der Prozentsatz zeigt, wie jeder Mitwirkende im Vergleich zu diesem Durchschnitt abschneidet.",

  scoreTeamTitle: "Team-Commit-Komplexitätsbewertung",
  scoreTeamDescription:
    "Kombinierte Komplexitätsbewertung aus allen Commits der Teammitglieder, die die technische Herausforderung und den Impact von Code-Änderungen misst. Höhere Bewertungen weisen auf komplexere und wirkungsvollere Arbeit hin.",

  // PRs tab
  prsTitle: "Zusammengeführte Pull Requests",
  prsDescription:
    "Gesamtzahl der erfolgreich zusammengeführten Pull Requests. Diese Metrik spiegelt die Teilnahme am Code-Review, Zusammenarbeit und erfolgreiche Bereitstellung von Features und Fixes wider.",
  prsAverage: "Durchschnitt: {value} PRs",
  prsComparison:
    " — Der Prozentsatz zeigt, wie jeder Mitwirkende im Vergleich zu diesem Durchschnitt abschneidet.",

  prsTeamTitle: "Team zusammengeführte Pull Requests",
  prsTeamDescription:
    "Gesamte zusammengeführte Pull Requests von allen Teammitgliedern. Dies spiegelt Teamzusammenarbeit, Teilnahme am Code-Review und erfolgreiche Bereitstellung von Features und Fixes wider.",

  // Performance tab - Insight cards
  insightMergedPRs: "Zusammengeführte PRs",
  insightPRThroughput: "PR-Durchsatz",
  insightFeatureCommits: "Feature-Commits",
  insightFeatures: "Features",
  insightBugfixCommits: "Bugfix-Commits",
  insightBugfixes: "Bugfixes",
  insightMaintenanceCommits: "Wartungs-Commits",
  insightMaintenance: "Wartung",

  // Chart titles
  chartCommitsByClassification: "Commits nach Klassifizierung",
  chartCommitsBySize: "Commits nach Größe",
  chartCommitScoresByWeek: "Commit-Bewertungen nach Woche",
  chartCommitScoresByWeekDesc:
    "Gesamte Commit-Komplexitätsbewertungen gruppiert nach Woche und gestapelt nach Größe",
  chartCommitScoresByClassificationWeek: "Commit-Bewertungen nach Klassifizierung",
  chartCommitScoresByClassificationWeekDesc:
    "Gesamte Commit-Komplexitätsbewertungen gruppiert nach Woche und gestapelt nach Klassifizierung",
  chartPRsMerged: "Diesen Monat zusammengeführte PRs",
  chartPRsMergedByWeek: "PRs nach Woche zusammengeführt",
  chartPRsMergedByWeekDesc:
    "Anzahl der zusammengeführten Pull Requests pro Woche für die letzten 6 Wochen",
  chartCodeChanges: "Code-Änderungen",
  chartCodeChangesByWeek: "Code-Änderungen nach Woche",
  chartCodeChangesByWeekDesc:
    "Hinzugefügte und entfernte Zeilen aus zusammengeführten PRs pro Woche",
  chartIssuesCreated: "Diesen Monat erstellte Issues",
  chartIssuesClosed: "Diesen Monat geschlossene Issues",
  chartActivityTitle: "Beitragsaktivität",
  chartActivityDescription:
    "Organisationsweite Aktivität einschließlich Commits, Pull Requests und Issues im letzten Jahr",
  chartDetails: "Details",

  // Chart labels
  linesAdded: "Zeilen hinzugefügt",
  linesRemoved: "Zeilen entfernt",

  // Language selector
  languageLabel: "Sprache",
  languageEnglish: "Englisch",
  languageGerman: "Deutsch",
  languageSpanish: "Spanisch",
  languageChinese: "Chinesisch",

  // DataTable
  dataTableFilters: "Filter",
  dataTableFilterBy: "Filtern nach:",
  dataTableNoResults: "Keine Ergebnisse.",
  dataTableRowsPerPage: "Zeilen pro Seite:",
  dataTablePage: "Seite",
  dataTableOf: "von",
  dataTableTotalRows: "Zeilen gesamt",
  dataTableSearch: "Suchen...",

  // Charts
  chartTrend: "Trend",
  chartDistribution: "Verteilung",
  chartDate: "Datum",

  // Quick insights
  quickInsightDate: "Letzte 7 Tage",
  quickInsightVsLastWeek: "vs {count} letzte Woche",
  quickInsightVsLastMonth: "vs {count} letzter Monat",

  // Data table column headers
  tableColumnTitle: "Titel",
  tableColumnMessage: "Nachricht",
  tableColumnAuthor: "Autor",
  tableColumnDate: "Datum",
  tableColumnCreated: "Erstellt",
  tableColumnScore: "Bewertung",
  tableColumnFiles: "Dateien",
  tableColumnAdditions: "+",
  tableColumnDeletions: "-",

  // Chart descriptions
  chartCommitsByClassificationDesc:
    "Anzahl der Commits gruppiert nach Klassifizierung (Commit-Typ) und gestapelt nach Komplexitätsgröße",
  chartCommitsBySizeDesc:
    "Anzahl der Commits gruppiert nach Komplexitätsgröße und gestapelt nach Klassifizierung (Commit-Typ)",
  chartCodeChangesDesc: "Hinzugefügte und entfernte Zeilen aus zusammengeführten PRs",
  chartPRsMergedDesc: "{count} PRs zusammengeführt (vs {prevCount} letzter Monat)",
  chartIssuesCreatedDesc: "{count} Issues erstellt (vs {prevCount} letzter Monat)",
  chartIssuesClosedDesc: "{count} Issues geschlossen (vs {prevCount} letzter Monat)",
}
